// src/features/imprint/stores/useCalendarStore.js
import { defineStore } from 'pinia';
import { useAppStore } from './useAppStore';
import { useImprintStore } from './useImprintStore';
import { 
    validateCalendarBundle, 
    getAvailablePrintMethods 
} from '../utils/calendarBundleValidation';
import { isSalePriceActive } from '../utils/calendarPricing';

/**
 * Calendar Store
 * Manages calendar products loaded from localStorage for calendar bundle mode
 */
export const useCalendarStore = defineStore('calendar', {
    state: () => ({
        // Product list loaded from localStorage
        products: [],
        
        // Quantity per product (key: product.id, value: qty)
        quantities: {},
        
        // Validation state
        isValid: false,
        
        // Loading state
        isLoading: false,
        
        // Error messages
        errors: [],
        
        // ========== CALENDAR BUNDLE COMPATIBILITY ==========
        // Available print methods that support ALL selected calendars
        availablePrintMethods: [],
        
        // Active tab index for calendar bundle (null if no available methods)
        activeTabForCalendarBundle: null
    }),
    
    getters: {
        /**
         * Get total quantity across all calendar products
         */
        totalQuantity(state) {
            return state.products.reduce((sum, product) => {
                return sum + (state.quantities[product.id] || 0);
            }, 0);
        },
        
        /**
         * Check if bundle compatibility check is required
         * Only for calendar application type
         */
        isBundleCheckRequired() {
            const appStore = useAppStore();
            console.log('[🔍 DEBUG CalendarStore] isBundleCheckRequired - applicationType:', appStore.applicationType);
            return appStore.applicationType === 'calendar';
        },
        
        /**
         * Check if bundle has compatibility issues (no available print methods)
         */
        hasBundleCompatibilityError(state) {
            const hasError = this.isBundleCheckRequired && 
                   state.availablePrintMethods.length === 0 && 
                   state.products.length > 0;
            console.log('[🔍 DEBUG CalendarStore] hasBundleCompatibilityError:', hasError, {
                isBundleCheckRequired: this.isBundleCheckRequired,
                availablePrintMethodsLength: state.availablePrintMethods.length,
                productsLength: state.products.length
            });
            return hasError;
        },
        
        /**
         * Calculate unit price based on total quantity using tier pricing
         * For calendar bundles - takes pricing from selected imprint method
         */
        unitPriceByTotalQty() {
            const totalQty = this.totalQuantity;
            const appStore = useAppStore();
            const imprintStore = useImprintStore();
            
            console.log('[🎯 CALENDAR-PRICING] === START ===');
            console.log('[🎯 CALENDAR-PRICING] totalQty:', totalQty);
            
            if (!appStore.full_product_json) {
                console.error('[🎯 CALENDAR-PRICING] ❌ full_product_json is NULL!');
                return 0;
            }
            
            // CRITICAL: For calendar bundles, tier pricing is in selected imprint method
            // Not in bundle product itself!
            let tierPrices = [];
            
            // Get selected imprint method SKU
            const selectedMethodSku = imprintStore.selectedImprintMethod?.sku;
            console.log('[🎯 CALENDAR-PRICING] Selected imprint method SKU:', selectedMethodSku);
            
            if (selectedMethodSku && appStore.full_product_json?.configurable_array?.[selectedMethodSku]) {
                // Calendar mode: Get tier prices from selected imprint method
                const methodData = appStore.full_product_json.configurable_array[selectedMethodSku];
                tierPrices = methodData.prices_2?.tier_price || [];
                console.log('[🎯 CALENDAR-PRICING] ✅ Found tier prices in imprint method:', selectedMethodSku);
                console.log('[🎯 CALENDAR-PRICING] tierPrices:', tierPrices);
            } else {
                console.error('[🎯 CALENDAR-PRICING] ❌ No imprint method selected or no tier prices!');
                console.error('[🎯 CALENDAR-PRICING] selectedMethodSku:', selectedMethodSku);
            }
            
            // Validate totalQty
            if (totalQty < 1 || isNaN(totalQty)) {
                console.error('[🎯 CALENDAR-PRICING] ❌ Invalid totalQty:', totalQty);
                return 0;
            }
            
            // Validate tierPrices
            if (!Array.isArray(tierPrices) || tierPrices.length === 0) {
                console.error('[🎯 CALENDAR-PRICING] ❌ No tier prices found!');
                console.error('[🎯 CALENDAR-PRICING] Check: imprint method selected AND prices_2.tier_price exists');
                return 0;
            }
            
            // Validate first tier has price
            if (!tierPrices[0] || typeof tierPrices[0].price === 'undefined') {
                console.error('[🎯 CALENDAR-PRICING] ❌ First tier has no price!');
                return 0;
            }
            
            // Default to first tier price
            let priceByQty = Number(tierPrices[0].price || 0);
            
            // Validate parsed price
            if (isNaN(priceByQty)) {
                console.error('[🎯 CALENDAR-PRICING] ❌ First tier price is NaN!');
                return 0;
            }
            
            console.log('[🎯 CALENDAR-PRICING] Default price (first tier):', priceByQty);
            
            // Find appropriate tier based on total quantity
            const sortedTiers = [...tierPrices]
                .filter(tier => tier.QtyMin !== 'MSRP')
                .sort((a, b) => Number(b.QtyMin || 0) - Number(a.QtyMin || 0));
            
            console.log('[🎯 CALENDAR-PRICING] Sorted tiers:', sortedTiers.map(t => ({
                QtyMin: t.QtyMin,
                price: t.price
            })));
            
            // Find matching tier
            let foundTier = false;
            let matchedTier = null;
            for (const tier of sortedTiers) {
                const tierMin = Number(tier.QtyMin || 0);

                if (Number(totalQty) >= tierMin) {
                    foundTier = true;
                    matchedTier = tier;
                    const regularPrice = Number(tier.price || 0);
                    if (!isNaN(regularPrice)) {
                        priceByQty = regularPrice;
                        console.log('[🎯 CALENDAR-PRICING] ✅ Found tier: QtyMin=' + tier.QtyMin + ', price=' + priceByQty);
                    }
                    break;
                }
            }

            // B2B override — sort ascending so last-match gives highest applicable tier
            const b2bTiers = selectedMethodSku ? appStore.getB2BTierForSku(selectedMethodSku) : null;
            if (b2bTiers) {
                const sortedB2b = [...b2bTiers].sort((a, b) => parseFloat(a.QtyMin || 0) - parseFloat(b.QtyMin || 0));
                let b2bEntry = null;
                sortedB2b.forEach(t => {
                    if (Number(totalQty) >= parseFloat(t.QtyMin || 0)) b2bEntry = t;
                });
                if (b2bEntry) {
                    const b2bPrice = parseFloat(b2bEntry.price);
                    if (!isNaN(b2bPrice)) {
                        console.log('[🎯 CALENDAR-PRICING] ✅ B2B price active. Using b2b price:', b2bPrice, '(was:', priceByQty, ')');
                        return b2bPrice; // B2B wins — skip sale price check
                    }
                }
            }

            // Honour sale_price when it is active for the matched tier
            if (matchedTier && isSalePriceActive(matchedTier)) {
                const salePrice = Number(matchedTier.sale_price);
                if (!isNaN(salePrice)) {
                    console.log('[🎯 CALENDAR-PRICING] ✅ Sale price active. Using sale_price:', salePrice, '(was:', priceByQty, ')');
                    priceByQty = salePrice;
                }
            }

            if (!foundTier) {
                console.warn('[🎯 CALENDAR-PRICING] ⚠️ No matching tier for qty:', totalQty);
            }
            
            // Final validation
            if (isNaN(priceByQty)) {
                console.error('[🎯 CALENDAR-PRICING] ❌ FINAL PRICE IS NaN!');
                return 0;
            }
            
            console.log('[🎯 CALENDAR-PRICING] === FINAL PRICE:', priceByQty, '===');
            return priceByQty;
        },
        
        /**
         * Get products with quantities merged and calculated unit price
         * CRITICAL: Always calculates unitPrice dynamically from tier pricing
         */
        productsWithQuantities(state) {
            // Get tier-based unit price for ALL calendars
            // This price is calculated based on TOTAL quantity
            const tierUnitPrice = this.unitPriceByTotalQty;
            
            return state.products.map(product => {
                // Remove old unitPrice from product data (if exists)
                const { unitPrice: oldPrice, ...productWithoutPrice } = product;
                
                return {
                    ...productWithoutPrice,
                    qty: state.quantities[product.id] || 0,
                    unitPrice: tierUnitPrice  // Always use calculated price
                };
            });
        },
        
        /**
         * Check if meets minimum quantity requirement
         */
        meetsMinimumQuantity() {
            const appStore = useAppStore();
            const minQty = appStore.getMinOrderQty;
            return this.totalQuantity >= minQty;
        },
        
        /**
         * Get validation errors
         */
        validationErrors(state) {
            const errors = [];
            const appStore = useAppStore();
            const minQty = appStore.getMinOrderQty;
            
            // Check minimum quantity
            if (this.totalQuantity < minQty) {
                errors.push(`Minimum order quantity is ${minQty}. Current total: ${this.totalQuantity}`);
            }
            
            // Check that all products have quantity
            const productsWithNoQty = state.products.filter(p => !state.quantities[p.id] || state.quantities[p.id] === 0);
            if (productsWithNoQty.length > 0) {
                errors.push(`Please set quantity for all selected calendars (${productsWithNoQty.length} calendar(s) have no quantity)`);
            }
            
            return errors;
        },
        
        /**
         * Check if any products loaded
         */
        hasProducts(state) {
            return state.products.length > 0;
        }
    },
    
    actions: {
        /**
         * Initialize calendar_sku for each product from full_product_json
         * Maps product title to calendar_sku from simple_attributes.calendar_sku
         */
        initCalendarSku() {
            const appStore = useAppStore();
            
            console.log('[🔍 DEBUG CalendarStore] initCalendarSku called');
            console.log('[🔍 DEBUG CalendarStore] full_product_json:', appStore.full_product_json);
            console.log('[🔍 DEBUG CalendarStore] simple_attributes:', appStore.full_product_json?.simple_attributes);
            
            const calendarSkuMap = appStore.full_product_json?.simple_attributes?.calendar_sku;
            
            console.log('[🔍 DEBUG CalendarStore] calendarSkuMap:', calendarSkuMap);
            
            if (!calendarSkuMap) {
                console.warn('[🔍 DEBUG CalendarStore] No calendar_sku mapping found in simple_attributes');
                return;
            }
            
            console.log('[🔍 DEBUG CalendarStore] Products before initCalendarSku:', JSON.stringify(this.products));
            
            if (this.products.length) {
                this.products.forEach((item, index) => {
                    const title = item.title?.trim();
                    console.log('[🔍 DEBUG CalendarStore] Looking for title:', title);
                    console.log('[🔍 DEBUG CalendarStore] Available titles in map:', Object.keys(calendarSkuMap));
                    
                    if (title && calendarSkuMap[title]) {
                        this.products[index].calendar_sku = calendarSkuMap[title];
                        console.log('[🔍 DEBUG CalendarStore] ✅ Set calendar_sku for', title, ':', calendarSkuMap[title]);
                    } else {
                        console.warn('[🔍 DEBUG CalendarStore] ❌ No calendar_sku found for title:', title);
                    }
                });
            }
            
            console.log('[🔍 DEBUG CalendarStore] Products after initCalendarSku:', JSON.stringify(this.products));
        },
        
        /**
         * Validate bundle compatibility with imprint methods
         */
        validateBundleCompatibility() {
            const appStore = useAppStore();
            
            console.log('[🔍 DEBUG CalendarStore] validateBundleCompatibility called');
            console.log('[🔍 DEBUG CalendarStore] applicationType:', appStore.applicationType);
            console.log('[🔍 DEBUG CalendarStore] products:', this.products);
            
            // Only check for calendar bundles
            if (!this.isBundleCheckRequired) {
                console.log('[🔍 DEBUG CalendarStore] Bundle check not required (not calendar mode)');
                return { isValid: true, availableMethods: [] };
            }
            
            // First, initialize calendar_sku for products
            this.initCalendarSku();
            
            const configurableArray = appStore.full_product_json?.configurable_array;
            
            console.log('[🔍 DEBUG CalendarStore] configurable_array:', configurableArray);
            
            if (!configurableArray) {
                console.warn('[🔍 DEBUG CalendarStore] No configurable_array found');
                return { isValid: true, availableMethods: [] };
            }
            
            // Get selected calendar SKUs
            const selectedSkus = this.products
                .map(item => item.calendar_sku)
                .filter(sku => sku);
            
            console.log('[🔍 DEBUG CalendarStore] Selected calendar SKUs:', selectedSkus);
            
            if (selectedSkus.length === 0) {
                console.warn('[🔍 DEBUG CalendarStore] No calendar SKUs found, assuming all methods available');
                this.availablePrintMethods = Object.keys(configurableArray);
                this.activeTabForCalendarBundle = 0;
                return { isValid: true, availableMethods: this.availablePrintMethods };
            }
            
            // Run validation
            const validation = validateCalendarBundle(configurableArray, this.products);
            
            // Store results
            this.availablePrintMethods = validation.availableMethods;
            
            console.log('[🔍 DEBUG CalendarStore] availablePrintMethods set to:', this.availablePrintMethods);
            
            // Find active tab index
            if (validation.isValid && this.availablePrintMethods.length > 0) {
                this.activeTabForCalendarBundle = 0;
            } else {
                this.activeTabForCalendarBundle = null;
            }
            
            console.log('[🔍 DEBUG CalendarStore] Final validation result:', {
                availableMethods: this.availablePrintMethods,
                isValid: validation.isValid,
                activeTabForCalendarBundle: this.activeTabForCalendarBundle
            });
            
            return validation;
        },
        
        /**
         * Check if a specific imprint method is available for the bundle
         */
        isMethodAvailableForBundle(methodSku) {
            if (!this.isBundleCheckRequired) {
                return true;
            }
            const isAvailable = this.availablePrintMethods.includes(methodSku);
            console.log('[🔍 DEBUG CalendarStore] isMethodAvailableForBundle', methodSku, ':', isAvailable, 'availableMethods:', this.availablePrintMethods);
            return isAvailable;
        },
        
        /**
         * Load calendar products from localStorage
         * Called on mount when in calendar mode
         */
        async loadFromLocalStorage() {
            this.isLoading = true;
            this.errors = [];
            
            try {
                console.log('[CalendarStore] Loading products from localStorage...');
                
                const storedData = localStorage.getItem('fullStorageProductList');
                
                if (!storedData) {
                    console.warn('[CalendarStore] No fullStorageProductList found in localStorage');
                    this.products = [];
                    this.quantities = {};
                    return;
                }
                
                const products = JSON.parse(storedData);
                
                if (!Array.isArray(products)) {
                    console.error('[CalendarStore] Invalid data format in localStorage (not an array)');
                    this.errors.push('Invalid calendar data format');
                    return;
                }
                
                this.products = products;
                
                // Initialize quantities from product data
                products.forEach(product => {
                    if (product.id && product.qty) {
                        this.quantities[product.id] = product.qty;
                    }
                });
                
                console.log('[CalendarStore] Loaded products:', {
                    count: products.length,
                    products: products,
                    quantities: this.quantities
                });
                
                // Validate after loading
                this.validate();
                
            } catch (error) {
                console.error('[CalendarStore] Error loading from localStorage:', error);
                this.errors.push('Failed to load calendar products from storage');
                this.products = [];
                this.quantities = {};
            } finally {
                this.isLoading = false;
            }
        },
        
        /**
         * Update quantity for a specific product
         * @param {number} productId - Product variant ID
         * @param {number} qty - New quantity
         */
        updateQuantity(productId, qty) {
            const parsedQty = parseInt(qty, 10);
            
            if (isNaN(parsedQty) || parsedQty < 0) {
                console.error('[CalendarStore] Invalid quantity:', qty);
                return;
            }
            
            this.quantities[productId] = parsedQty;
            console.log('[CalendarStore] Updated quantity:', { productId, qty: parsedQty });
            
            this.validate();
            this.saveToLocalStorage();
        },
        
        /**
         * Save current state back to localStorage
         * Keeps localStorage in sync with store state
         * CRITICAL: Never save unitPrice to localStorage - always calculate dynamically
         */
        saveToLocalStorage() {
            try {
                // Save products WITHOUT unitPrice (it's always calculated)
                const productsToSave = this.products.map(product => {
                    // Remove unitPrice before saving
                    const { unitPrice, ...productData } = product;
                    return {
                        ...productData,
                        qty: this.quantities[product.id] || 0
                    };
                });
                
                localStorage.setItem('fullStorageProductList', JSON.stringify(productsToSave));
                console.log('[CalendarStore] Saved to localStorage (without unitPrice):', productsToSave);
            } catch (error) {
                console.error('[CalendarStore] Error saving to localStorage:', error);
            }
        },
        
        /**
         * Validate current state
         * Updates isValid flag and returns validation result
         */
        validate() {
            const errors = this.validationErrors;
            this.isValid = errors.length === 0;
            
            console.log('[CalendarStore] Validation result:', {
                isValid: this.isValid,
                errors: errors,
                totalQty: this.totalQuantity,
                productsCount: this.products.length
            });
            
            return this.isValid;
        },
        
        /**
         * Clear all calendar data
         * Removes data from store and localStorage
         */
        clear() {
            console.log('[CalendarStore] Clearing all data');
            
            this.products = [];
            this.quantities = {};
            this.isValid = false;
            this.errors = [];
            this.availablePrintMethods = [];
            this.activeTabForCalendarBundle = null;
            
            // Clear localStorage
            localStorage.removeItem('fullStorageProductList');
            localStorage.removeItem('storageProductList');
        },
        
        /**
         * Reload products from localStorage
         * Useful for refreshing data
         */
        async reload() {
            console.log('[CalendarStore] Reloading products...');
            await this.loadFromLocalStorage();
        }
    }
});
